// File: frontend/src/utils/api.js
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const DEFAULT_TIMEOUT_MS = 30000;

// Generic fetch wrapper with error handling and timeout
const fetchAPI = async (endpoint, options = {}) => {
  const { timeout = DEFAULT_TIMEOUT_MS, responseType, ...fetchOptions } = options;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      credentials: 'include',
      ...fetchOptions,
      headers: {
        'Content-Type': 'application/json',
        ...fetchOptions.headers,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.error || `HTTP error! status: ${response.status}`);
      // Callers need the status to tell "no permission" (403) apart from
      // "backend down" — treating them the same locked non-admin users out
      // with a false "System Offline" (issue #58)
      error.status = response.status;
      throw error;
    }

    if (responseType === 'blob') {
      return response;
    }

    const data = await response.json();

    // When caller requests pagination metadata, return it alongside the data
    if (options.returnMeta) {
      return {
        data,
        meta: {
          total: parseInt(response.headers.get('X-Total-Count') || '0', 10),
          hasMore: response.headers.get('X-Has-More') === 'true',
        },
      };
    }

    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      const msg = `Request timed out (${endpoint})`;
      console.error(msg);
      throw new Error(msg);
    }
    console.error(`API Error (${endpoint}):`, error);
    throw error;
  }
};

// People API
export const peopleAPI = {
  getAll: ({ limit = 100, offset = 0, ...rest } = {}) =>
    fetchAPI(`/people${buildQuery({ limit, offset, ...rest })}`, { returnMeta: true }),

  getById: (id) => fetchAPI(`/people/${id}`),

  // Appends OSINT data entries server-side (issue #96); duplicates are skipped.
  addOsint: (id, entries) => fetchAPI(`/people/${id}/osint`, { method: 'POST', body: JSON.stringify({ entries }) }),

  create: (personData) => fetchAPI('/people', {
    method: 'POST',
    body: JSON.stringify(personData),
  }),
  
  update: (id, personData) => fetchAPI(`/people/${id}`, {
    method: 'PUT',
    body: JSON.stringify(personData),
  }),
  
  delete: (id) => fetchAPI(`/people/${id}`, {
    method: 'DELETE',
  }),
};

// Businesses API
export const businessesAPI = {
  getAll: (params = {}) => fetchAPI(`/businesses${buildQuery(params)}`),
  
  create: (businessData) => fetchAPI('/businesses', {
    method: 'POST',
    body: JSON.stringify(businessData),
  }),
  
  update: (id, businessData) => fetchAPI(`/businesses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(businessData),
  }),
  
  delete: (id) => fetchAPI(`/businesses/${id}`, {
    method: 'DELETE',
  }),
};

// Tools API
export const toolsAPI = {
  // Bulk import: one request rather than one per row, so a few hundred tools
  // don't trip the rate limiter.
  bulkImport: (payload) => fetchAPI('/tools/bulk-import', {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  // params: { project_id, all } — all=true (admins) lists every tool.
  getAll: (params = {}) => fetchAPI(`/tools${buildQuery(params)}`),
  // Integrated tools (issue #96). Keys are write-only: there is no getter.
  setKey: (id, scope, secret) => fetchAPI(`/tools/${id}/credentials`, { method: 'PUT', body: JSON.stringify({ scope, secret }) }),
  removeKey: (id, scope) => fetchAPI(`/tools/${id}/credentials?scope=${scope}`, { method: 'DELETE' }),
  run: (id, payload) => fetchAPI(`/tools/${id}/run`, { method: 'POST', body: JSON.stringify(payload), timeout: 90000 }),
  test: (id) => fetchAPI(`/tools/${id}/test`, { method: 'POST', timeout: 90000 }),
  exportFile: (payload) => fetchAPI('/tools/export', { method: 'POST', body: JSON.stringify(payload) }),
  importFile: (payload) => fetchAPI('/tools/import', { method: 'POST', body: JSON.stringify(payload), timeout: 120000 }),
  getSettings: () => fetchAPI('/settings/tools'),
  updateSettings: (payload) => fetchAPI('/settings/tools', { method: 'PUT', body: JSON.stringify(payload) }),
  
  create: (toolData) => fetchAPI('/tools', {
    method: 'POST',
    body: JSON.stringify(toolData),
  }),
  
  update: (id, toolData) => fetchAPI(`/tools/${id}`, {
    method: 'PUT',
    body: JSON.stringify(toolData),
  }),
  
  delete: (id) => fetchAPI(`/tools/${id}`, {
    method: 'DELETE',
  }),
};

// Todos API
export const todosAPI = {
  getAll: (params = {}) => fetchAPI(`/todos${buildQuery(params)}`),
  
  create: (todoData) => fetchAPI('/todos', {
    method: 'POST',
    body: JSON.stringify(todoData),
  }),
  
  update: (id, updates) => fetchAPI(`/todos/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),
  
  delete: (id) => fetchAPI(`/todos/${id}`, {
    method: 'DELETE',
  }),
};

// Cases API
export const casesAPI = {
  getAll: (params = {}) => fetchAPI(`/cases${buildQuery(params)}`),
  
  create: (caseData) => fetchAPI('/cases', {
    method: 'POST',
    body: JSON.stringify(caseData),
  }),
  
  update: (id, caseData) => fetchAPI(`/cases/${id}`, {
    method: 'PUT',
    body: JSON.stringify(caseData),
  }),
  
  delete: (id) => fetchAPI(`/cases/${id}`, {
    method: 'DELETE',
  }),
};

// Projects API (issue #83) — the hard isolation boundary; cases nest inside a project.
export const projectsAPI = {
  getAll: () => fetchAPI('/projects'),

  create: (projectData) => fetchAPI('/projects', {
    method: 'POST',
    body: JSON.stringify(projectData),
  }),

  update: (id, projectData) => fetchAPI(`/projects/${id}`, {
    method: 'PUT',
    body: JSON.stringify(projectData),
  }),

  // Per-entity row counts for the delete-confirmation dialog (issue #88).
  getStats: (id) => fetchAPI(`/projects/${id}/stats`),

  // Cascade delete (issue #88). When the project holds data the backend
  // requires confirmName to equal the project's exact name.
  delete: (id, confirmName) => fetchAPI(`/projects/${id}`, {
    method: 'DELETE',
    body: JSON.stringify(confirmName != null ? { confirm_name: confirmName } : {}),
  }),
};

// Project Members API (issue #84)
export const projectMembersAPI = {
  getAll: (projectId) => fetchAPI(`/projects/${projectId}/members`),
  add: (projectId, data) => fetchAPI(`/projects/${projectId}/members`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateRole: (projectId, userId, project_role) => fetchAPI(`/projects/${projectId}/members/${userId}`, {
    method: 'PUT',
    body: JSON.stringify({ project_role }),
  }),
  remove: (projectId, userId) => fetchAPI(`/projects/${projectId}/members/${userId}`, {
    method: 'DELETE',
  }),
};

// Custom Fields API
export const customFieldsAPI = {
  getAll: () => fetchAPI('/settings/custom-fields'),
  
  create: (fieldData) => fetchAPI('/settings/custom-fields', {
    method: 'POST',
    body: JSON.stringify(fieldData),
  }),
  
  update: (id, fieldData) => fetchAPI(`/settings/custom-fields/${id}`, {
    method: 'PUT',
    body: JSON.stringify(fieldData),
  }),
  
  delete: (id) => fetchAPI(`/settings/custom-fields/${id}`, {
    method: 'DELETE',
  }),
};

// Model Options API
// Running version + whether a newer release exists. The check runs on the
// server; the browser never contacts GitHub.
export const versionAPI = {
  get: () => fetchAPI('/version'),
};

export const updateSettingsAPI = {
  get: () => fetchAPI('/settings/updates'),
  update: (payload) => fetchAPI('/settings/updates', {
    method: 'PUT',
    body: JSON.stringify(payload),
  }),
};

// Archived-project retention policy (issue #88). retentionDays: 0 = never.
// Server-side global app settings (issue #91): branding + system-wide
// defaults for language/theme. GET needs no auth — the login screen and the
// first app paint need it before we know who's signed in.
export const brandingAPI = {
  get: () => fetchAPI('/settings/branding'),
  update: (data) => fetchAPI('/settings/branding', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

export const projectRetentionAPI = {
  get: () => fetchAPI('/settings/project-retention'),
  update: (retentionDays) => fetchAPI('/settings/project-retention', {
    method: 'PUT',
    body: JSON.stringify({ retentionDays }),
  }),
};

// Geocoding provider config (issue #62). The API key is write-only server
// side — get() reports only whether one is stored, never its value.
export const geocodingSettingsAPI = {
  get: () => fetchAPI('/settings/geocoding'),
  update: (payload) => fetchAPI('/settings/geocoding', {
    method: 'PUT',
    body: JSON.stringify(payload),
  }),
};

export const modelOptionsAPI = {
  getAll: () => fetchAPI('/settings/model-options'),
  
  create: (optionData) => fetchAPI('/settings/model-options', {
    method: 'POST',
    body: JSON.stringify(optionData),
  }),
  
  update: (id, optionData) => fetchAPI(`/settings/model-options/${id}`, {
    method: 'PUT',
    body: JSON.stringify(optionData),
  }),
  
  delete: (id) => fetchAPI(`/settings/model-options/${id}`, {
    method: 'DELETE',
  }),
};

// Export/Import API - Fixed export function
export const exportAPI = {
  export: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/export`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Get the JSON data
      const data = await response.json();
      
      // Create a blob from the JSON data
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `osint-crm-export-${new Date().getTime()}.json`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      return data;
    } catch (error) {
      console.error('Error exporting data:', error);
      throw error;
    }
  },
};

export const importAPI = {
  import: (data, projectId) => fetchAPI(`/import${buildQuery({ project_id: projectId })}`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Logo Upload API
export const uploadLogo = async (file) => {
  const formData = new FormData();
  formData.append('appLogo', file);

  try {
    const response = await fetch(`${API_BASE_URL}/upload/logo`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Logo upload failed');
    }

    const data = await response.json();
    return `${API_BASE_URL.replace('/api', '')}${data.logoUrl}`;
  } catch (error) {
    console.error('Error uploading logo:', error);
    throw error;
  }
};

// Locations API
export const locationsAPI = {
  getAll: (params = {}) => fetchAPI(`/locations${buildQuery(params)}`),
};

// Travel History API
export const travelHistoryAPI = {
  getByPerson: (personId) => fetchAPI(`/people/${personId}/travel-history`),
  
  create: (personId, travelData) => fetchAPI(`/people/${personId}/travel-history`, {
    method: 'POST',
    body: JSON.stringify(travelData),
  }),
  
  update: (travelId, travelData) => fetchAPI(`/travel-history/${travelId}`, {
    method: 'PUT',
    body: JSON.stringify(travelData),
  }),
  
  delete: (travelId) => fetchAPI(`/travel-history/${travelId}`, {
    method: 'DELETE',
  }),
  
  analyze: (personId) => fetchAPI(`/people/${personId}/travel-analysis`),
};

// Business API
export const businessAPI = {
  getAll: (params = {}) => fetchAPI(`/businesses${buildQuery(params)}`),
  
  getById: (id) => fetchAPI(`/businesses/${id}`),
  
  create: (businessData) => fetchAPI('/businesses', {
    method: 'POST',
    body: JSON.stringify(businessData),
  }),
  
  update: (id, businessData) => fetchAPI(`/businesses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(businessData),
  }),
  
  delete: (id) => fetchAPI(`/businesses/${id}`, {
    method: 'DELETE',
  }),
};

// System Health API
export const systemAPI = {
  getHealth: () => fetchAPI('/system/health'),
};

// Wireless Networks API (WiGLE Integration)
export const wirelessNetworksAPI = {
  getAll: (params) => {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          queryParams.append(key, value);
        }
      });
    }
    return fetchAPI(`/wireless-networks?${queryParams.toString()}`);
  },

  getById: (id) => fetchAPI(`/wireless-networks/${id}`),

  create: (data) => fetchAPI('/wireless-networks', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (id, data) => fetchAPI(`/wireless-networks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  delete: (id) => fetchAPI(`/wireless-networks/${id}`, {
    method: 'DELETE',
  }),

  bulkDelete: (ids, projectId) => fetchAPI('/wireless-networks/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ ids, project_id: projectId }),
  }),

  importKML: async (file, projectId) => {
    const formData = new FormData();
    formData.append('kmlFile', file);
    if (projectId) formData.append('project_id', projectId);

    const response = await fetch(`${API_BASE_URL}/wireless-networks/import-kml`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Import failed');
    }

    return response.json();
  },

  getStats: (params = {}) => fetchAPI(`/wireless-networks/stats${buildQuery(params)}`),

  getNearby: (latitude, longitude, radius = 0.5) =>
    fetchAPI(`/wireless-networks/nearby?latitude=${latitude}&longitude=${longitude}&radius=${radius}`),

  associate: (id, personId, note, confidence) => fetchAPI(`/wireless-networks/${id}/associate`, {
    method: 'POST',
    body: JSON.stringify({
      person_id: personId,
      association_note: note,
      association_confidence: confidence,
    }),
  }),

  removeAssociation: (id) => fetchAPI(`/wireless-networks/${id}/associate`, {
    method: 'DELETE',
  }),
};

// Helper to build a query string from a params object (skips empty values)
const buildQuery = (params = {}) => {
  const qp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') qp.append(key, value);
  });
  const s = qp.toString();
  return s ? `?${s}` : '';
};

// Properties API (transaction tracking — issue #43)
export const propertiesAPI = {
  getAll: (params = {}) => fetchAPI(`/properties${buildQuery(params)}`),
  getById: (id) => fetchAPI(`/properties/${id}`),
  create: (data) => fetchAPI('/properties', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => fetchAPI(`/properties/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id) => fetchAPI(`/properties/${id}`, { method: 'DELETE' }),
  getTransactions: (id) => fetchAPI(`/properties/${id}/transactions`),
  getLedger: (id, params = {}) => fetchAPI(`/properties/${id}/ledger${buildQuery(params)}`),
};

// Assets API
export const assetsAPI = {
  getAll: (params = {}) => fetchAPI(`/assets${buildQuery(params)}`),
  getById: (id) => fetchAPI(`/assets/${id}`),
  create: (data) => fetchAPI('/assets', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => fetchAPI(`/assets/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id) => fetchAPI(`/assets/${id}`, { method: 'DELETE' }),
  getByPerson: (personId) => fetchAPI(`/people/${personId}/assets`),
};

// Crypto Wallets API (issue #82)
export const cryptoWalletsAPI = {
  getAll: (params = {}) => fetchAPI(`/crypto-wallets${buildQuery(params)}`),
  getById: (id) => fetchAPI(`/crypto-wallets/${id}`),
  create: (data) => fetchAPI('/crypto-wallets', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => fetchAPI(`/crypto-wallets/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id) => fetchAPI(`/crypto-wallets/${id}`, { method: 'DELETE' }),
};

// Files API (issue #97). Uploads go through XMLHttpRequest rather than
// fetchAPI: fetch can't report upload progress, and fetchAPI's 30 s timeout
// would cut off a large upload.
export const filesAPI = {
  getAll: (params = {}) => fetchAPI(`/files${buildQuery(params)}`),
  update: (id, data) => fetchAPI(`/files/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id) => fetchAPI(`/files/${id}`, { method: 'DELETE' }),

  // fields: { project_id?, person_id?, description? }. onProgress gets 0..1.
  upload: (file, fields = {}, onProgress) => new Promise((resolve, reject) => {
    const form = new FormData();
    Object.entries(fields).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') form.append(k, v);
    });
    form.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE_URL}/files`);
    xhr.withCredentials = true;
    if (onProgress) {
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    }
    xhr.onload = () => {
      let body = {};
      try { body = JSON.parse(xhr.responseText || '{}'); } catch { /* non-JSON error page */ }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(body);
      const error = new Error(body.error || `HTTP error! status: ${xhr.status}`);
      error.status = xhr.status;
      error.limitBytes = body.limit_bytes;
      reject(error);
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(form);
  }),

  // The file's bytes as a Blob. preview: true is logged as a view rather than
  // a download. Downloads also go through here (not a plain link) so they
  // work behind the dev-server proxy, which doesn't forward page navigations.
  fetchContent: async (id, { preview = false } = {}) => {
    const response = await fetchAPI(`/files/${id}/content${preview ? '?preview=1' : ''}`, {
      responseType: 'blob',
      timeout: 5 * 60 * 1000,
    });
    return response.blob();
  },
};

// Saves a Blob to the user's computer under the given name.
export const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'file';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
};

// Integration manifests behind integrated tools (issue #96).
export const integrationsAPI = {
  getAll: () => fetchAPI('/integrations'),
  get: (id) => fetchAPI(`/integrations/${encodeURIComponent(id)}`),
  install: (manifest, replace = false) => fetchAPI('/integrations', { method: 'POST', body: JSON.stringify({ manifest, replace }) }),
  remove: (id) => fetchAPI(`/integrations/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  // Integration builder: one test call for an uninstalled draft, and a
  // mapping preview that makes no outbound request.
  draftTest: (payload) => fetchAPI('/integrations/draft/test', { method: 'POST', body: JSON.stringify(payload), timeout: 90000 }),
  draftMap: (payload) => fetchAPI('/integrations/draft/map', { method: 'POST', body: JSON.stringify(payload) }),
};

// Relationships API (issue #83's write-of-record table; issue #82's wallet
// links are the first frontend consumer to read/write it directly rather
// than through the person/business JSONB sync path).
export const relationshipsAPI = {
  getAll: (params = {}) => fetchAPI(`/relationships${buildQuery(params)}`),
  create: (data) => fetchAPI('/relationships', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => fetchAPI(`/relationships/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id) => fetchAPI(`/relationships/${id}`, { method: 'DELETE' }),
};

// Transactions API
export const transactionsAPI = {
  getAll: (params = {}) => fetchAPI(`/transactions${buildQuery(params)}`),
  getById: (id) => fetchAPI(`/transactions/${id}`),
  create: (data) => fetchAPI('/transactions', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => fetchAPI(`/transactions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id) => fetchAPI(`/transactions/${id}`, { method: 'DELETE' }),
  getByPerson: (personId) => fetchAPI(`/people/${personId}/transactions`),
  getByBusiness: (businessId) => fetchAPI(`/businesses/${businessId}/transactions`),
  getByProperty: (propertyId) => fetchAPI(`/properties/${propertyId}/transactions`),
};

// Entity ledger + venue analytics API
export const ledgerAPI = {
  get: (entityType, id, params = {}) => fetchAPI(`/${entityType}/${id}/ledger${buildQuery(params)}`),
  venueStats: (businessId) => fetchAPI(`/businesses/${businessId}/venue-stats`),
};

export { API_BASE_URL };
// Scratchpad API — the caller's private notepad (not project-scoped)
export const scratchpadAPI = {
  get: () => fetchAPI('/scratchpad'),
  addEntry: (content, source_label = null) => fetchAPI('/scratchpad/entries', {
    method: 'POST',
    body: JSON.stringify({ content, source_label }),
  }),
  updateEntry: (id, content) => fetchAPI(`/scratchpad/entries/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ content }),
  }),
  // keepalive lets a delete still go out while the page is closing
  deleteEntry: (id, keepalive = false) => fetchAPI(`/scratchpad/entries/${id}`, { method: 'DELETE', keepalive }),
  clear: (upToId, keepalive = false) => fetchAPI(`/scratchpad/entries${buildQuery({ up_to_id: upToId })}`, { method: 'DELETE', keepalive }),
  updateSettings: (settings) => fetchAPI('/scratchpad/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  }),
};
